/**
 * Recurring show occurrence helpers (local calendar dates, Monday-based weeks).
 * Shared by client (via webpack) and Node tests.
 */

const MAX_RECURRING_SHOWS = 52;

/** JS getDay() order for UI Mon → Sun */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function parseYmdLocal(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

function formatYmdLocal(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function addDaysLocal(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return x;
}

function mondayOfWeekContaining(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function daysBetweenUTC(a, b) {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ua - ub) / 86400000);
}

function selectedWeekdaySet(weekdaysMonSun) {
  const s = new Set();
  if (!Array.isArray(weekdaysMonSun)) return s;
  weekdaysMonSun.forEach((on, i) => {
    if (on && WEEKDAY_ORDER[i] !== undefined) {
      s.add(WEEKDAY_ORDER[i]);
    }
  });
  return s;
}

function getEndExclusive(startDateStr, endPreset, endDateStr) {
  const start = parseYmdLocal(startDateStr);
  if (!start) return null;

  if (endPreset === "on_date") {
    const end = parseYmdLocal(endDateStr);
    if (!end) return null;
    return addDaysLocal(end, 1);
  }

  if (endPreset === "1m") {
    return new Date(start.getFullYear(), start.getMonth() + 1, start.getDate());
  }
  if (endPreset === "3m") {
    return new Date(start.getFullYear(), start.getMonth() + 3, start.getDate());
  }
  if (endPreset === "6m") {
    return new Date(start.getFullYear(), start.getMonth() + 6, start.getDate());
  }
  if (endPreset === "1y") {
    return new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
  }
  return null;
}

function buildOccurrenceDates(startDateStr, options) {
  const {
    intervalMode = "1",
    customWeeks = 1,
    weekdaysMonSun = [false, false, false, false, false, false, false],
    endPreset = "1m",
    endDateStr = "",
  } = options || {};

  const start = parseYmdLocal(startDateStr);
  if (!start) return [];

  let intervalWeeks = 1;
  if (intervalMode === "custom") {
    const n = Number.parseInt(String(customWeeks), 10);
    intervalWeeks = Number.isNaN(n) ? 1 : Math.min(12, Math.max(1, n));
  } else {
    const n = Number.parseInt(String(intervalMode), 10);
    intervalWeeks = Number.isNaN(n) ? 1 : Math.min(4, Math.max(1, n));
  }

  const selected = selectedWeekdaySet(weekdaysMonSun);
  if (selected.size === 0) return [];

  const endExclusive = getEndExclusive(startDateStr, endPreset, endDateStr);
  if (!endExclusive || endExclusive <= start) return [];

  const mondayStart = mondayOfWeekContaining(start);
  const out = [];

  for (
    let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    cursor < endExclusive;
    cursor = addDaysLocal(cursor, 1)
  ) {
    const dow = cursor.getDay();
    if (!selected.has(dow)) continue;

    const mondayCursor = mondayOfWeekContaining(cursor);
    const weekDiff = daysBetweenUTC(mondayCursor, mondayStart) / 7;
    if (weekDiff < 0 || weekDiff % intervalWeeks !== 0) continue;

    out.push(formatYmdLocal(cursor));
  }

  return out;
}

function defaultWeekdaysMonSun() {
  return [false, false, false, false, false, false, false];
}

function weekdaysFromGetDay(getDay) {
  const w = defaultWeekdaysMonSun();
  const idx = WEEKDAY_ORDER.indexOf(getDay);
  if (idx >= 0) w[idx] = true;
  return w;
}

module.exports = {
  MAX_RECURRING_SHOWS,
  WEEKDAY_ORDER,
  WEEKDAY_LABELS,
  parseYmdLocal,
  formatYmdLocal,
  mondayOfWeekContaining,
  selectedWeekdaySet,
  getEndExclusive,
  buildOccurrenceDates,
  defaultWeekdaysMonSun,
  weekdaysFromGetDay,
};
